import { useEffect, useRef, useCallback } from 'react';

export interface UseAccessibilityOptions {
  announcePageChanges?: boolean;
  manageFocus?: boolean;
  announceLoadingStates?: boolean;
}

export function useAccessibility(options: UseAccessibilityOptions = {}) {
  const announcementRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Create live region for screen reader announcements
  useEffect(() => {
    if (options.announcePageChanges || options.announceLoadingStates) {
      // Create live region if it doesn't exist
      let liveRegion = document.getElementById('sr-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'sr-live-region';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'sr-only';
        document.body.appendChild(liveRegion);
      }
      announcementRef.current = liveRegion as HTMLDivElement;
    }

    return () => {
      // Clean up if this is the last component using announcements
      const liveRegion = document.getElementById('sr-live-region');
      if (liveRegion) {
        liveRegion.remove();
      }
    };
  }, [options.announcePageChanges, options.announceLoadingStates]);

  // Announce messages to screen readers
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announcementRef.current) {
      // Clear previous message
      announcementRef.current.textContent = '';
      // Set new message with small delay to ensure it's announced
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.setAttribute('aria-live', priority);
          announcementRef.current.textContent = message;
        }
      }, 100);
    }
  }, []);

  // Focus management utilities
  const saveFocus = useCallback(() => {
    if (options.manageFocus) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [options.manageFocus]);

  const restoreFocus = useCallback(() => {
    if (options.manageFocus && previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [options.manageFocus]);

  const focusElement = useCallback((element: HTMLElement | string) => {
    if (typeof element === 'string') {
      const targetElement = document.getElementById(element) || document.querySelector(element);
      if (targetElement) {
        (targetElement as HTMLElement).focus();
      }
    } else {
      element.focus();
    }
  }, []);

  // Keyboard navigation helper
  const handleKeyboardNavigation = useCallback((
    event: React.KeyboardEvent,
    callbacks: {
      onEnter?: () => void;
      onSpace?: () => void;
      onArrowUp?: () => void;
      onArrowDown?: () => void;
      onArrowLeft?: () => void;
      onArrowRight?: () => void;
      onEscape?: () => void;
      onTab?: () => void;
    }
  ) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        callbacks.onEnter?.();
        break;
      case ' ':
        event.preventDefault();
        callbacks.onSpace?.();
        break;
      case 'ArrowUp':
        event.preventDefault();
        callbacks.onArrowUp?.();
        break;
      case 'ArrowDown':
        event.preventDefault();
        callbacks.onArrowDown?.();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        callbacks.onArrowLeft?.();
        break;
      case 'ArrowRight':
        event.preventDefault();
        callbacks.onArrowRight?.();
        break;
      case 'Escape':
        event.preventDefault();
        callbacks.onEscape?.();
        break;
      case 'Tab':
        callbacks.onTab?.();
        break;
    }
  }, []);

  // Loading state announcer
  const announceLoading = useCallback((isLoading: boolean, loadingMessage = 'Loading...', completedMessage = 'Content loaded') => {
    if (options.announceLoadingStates) {
      if (isLoading) {
        announce(loadingMessage);
      } else {
        announce(completedMessage);
      }
    }
  }, [announce, options.announceLoadingStates]);

  // Page change announcer
  const announcePageChange = useCallback((pageName: string) => {
    if (options.announcePageChanges) {
      announce(`Navigated to ${pageName}`, 'polite');
    }
  }, [announce, options.announcePageChanges]);

  return {
    announce,
    saveFocus,
    restoreFocus,
    focusElement,
    handleKeyboardNavigation,
    announceLoading,
    announcePageChange,
  };
}